import api from '../api';

// cccd-event-attendance-tickets: cliente para el escaneo/check-in en Gestión.
// Ver platforms/api/src/routes/cccd-attendance.ts.

export interface AttendeeTicket {
  id: string;
  qrToken: string;
  attendeeType: 'member' | 'companion';
  firstName: string;
  lastName: string;
  documentType: string;
  documentNumber: string;
  status: 'active' | 'revoked';
  team?: { id: string; name: string };
}

export interface AttendeeCheckIn {
  id: string;
  attendeeId: string;
  eventDate: string;
  checkedInAt: string | null;
  checkedInById: string | null;
  wristbandGivenAt: string | null;
  wristbandGivenById: string | null;
  method: 'qr_scan' | 'document_lookup';
}

export interface AttendanceReport {
  totalTickets: number;
  totalMembers: number;
  totalCompanions: number;
  days: Array<{ eventDate: string; checkedIn: number; wristbands: number; members: number; companions: number }>;
}

/** Una fila por evento nacional o regional, con ceros explícitos (GET /cccd/attendance/summary). */
export interface AttendanceSummaryRow {
  scope: 'event' | 'regional';
  id: string;
  name: string;
  slug: string;
  region: string | null;
  eventStartDate: string;
  eventEndDate: string;
  isActive: boolean;
  attendeeTicketsEnabled: boolean;
  totalTickets: number;
  totalMembers: number;
  totalCompanions: number;
  checkedIn: number;
  attendeesCheckedIn: number;
  wristbands: number;
  checkedInToday: number;
}

function data<T>(res: { data: { data: T } }): T {
  return res.data.data;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export const cccdAttendance = {
  todayIso,

  getTicketByQr: (qrToken: string, eventDate = todayIso()) =>
    api
      .get<{ data: AttendeeTicket & { team: { id: string; name: string }; checkIn: AttendeeCheckIn | null } }>(
        `/cccd/attendance/ticket/${qrToken}`,
        { params: { eventDate } },
      )
      .then(data),

  lookupByDocument: (documentNumber: string, scope: { eventId?: string; regionalEventId?: string }, eventDate = todayIso()) =>
    api
      .get<{ data: AttendeeTicket & { team: { id: string; name: string }; checkIn: AttendeeCheckIn | null } }>(
        '/cccd/attendance/lookup',
        { params: { documentNumber, ...scope, eventDate } },
      )
      .then(data),

  checkin: (body: {
    qrToken?: string;
    attendeeId?: string;
    eventDate: string;
    method: 'qr_scan' | 'document_lookup';
    wristbandGiven?: boolean;
  }) => api.post<{ data: AttendeeCheckIn }>('/cccd/attendance/checkin', body).then(data),

  setWristband: (checkInId: string, wristbandGiven: boolean) =>
    api.patch<{ data: AttendeeCheckIn }>(`/cccd/attendance/checkin/${checkInId}/wristband`, { wristbandGiven }).then(data),

  registerOnsite: (body: {
    eventId?: string;
    regionalEventId?: string;
    teamId: string;
    firstName: string;
    lastName: string;
    documentType: string;
    documentNumber: string;
    email?: string;
    phone?: string;
    relationshipToTeam?: string;
    marketingConsent: boolean;
    dataTreatmentAuthorization: boolean;
    eventDate: string;
  }) =>
    api
      .post<{ data: AttendeeTicket & { checkIn: AttendeeCheckIn; alreadyCheckedIn: boolean } }>('/cccd/attendance/onsite', body)
      .then(data),

  searchTeams: (q: string, scope: { eventId?: string; regionalEventId?: string }) =>
    api
      .get<{ data: Array<{ id: string; name: string }> }>('/cccd/attendance/teams/search', { params: { q, ...scope } })
      .then(data),

  getSummary: () => api.get<{ data: AttendanceSummaryRow[] }>('/cccd/attendance/summary').then(data),

  getReport: (scope: { eventId?: string; regionalEventId?: string }) =>
    api.get<{ data: AttendanceReport }>('/cccd/attendance/report', { params: scope }).then(data),
};
